import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, SectionLabel } from "@/components/Primitives";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin",            label: "Admin" },
  { value: "analyst",          label: "Analyst" },
  { value: "campaign_manager", label: "Campaign Manager" },
];

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "analyst" });

  const load = async () => {
    const { data } = await api.get("/auth/users");
    setUsers(data);
  };

  useEffect(() => {
    (async () => { try { await load(); } finally { setLoading(false); } })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post("/auth/register", { ...form, email: form.email.trim().toLowerCase() });
      await load();
      setForm({ name: "", email: "", password: "", role: "analyst" });
      toast.success("User added");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1400px]">
      <PageHeader
        eyebrow="Admin"
        title="Internal Users"
        description="Add and review internal accounts. Only admins can manage users."
        testid="users-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <form onSubmit={submit} className="lg:col-span-5 bg-white border border-[#E0E0D8] p-6 h-fit" data-testid="add-user-form">
          <SectionLabel>Add user</SectionLabel>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Name</label>
              <input data-testid="user-name-input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"/>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Email</label>
              <input data-testid="user-email-input" required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"/>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Password</label>
              <input data-testid="user-password-input" required type="password" minLength={6} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"/>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Role</label>
              <select data-testid="user-role-select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {error && <p data-testid="user-form-error" className="text-xs font-mono text-[#D91919]">{error}</p>}
            <button type="submit" disabled={submitting} data-testid="user-submit-button"
              className="w-full bg-[#FF3E00] text-white py-3 font-semibold hover:bg-[#D93500] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting ? <Loader2 className="animate-spin" size={16}/> : <UserPlus size={16}/>}
              {submitting ? "Adding…" : "Add user"}
            </button>
          </div>
        </form>

        <div className="lg:col-span-7">
          <SectionLabel>All users ({users.length})</SectionLabel>
          {loading ? (
            <p className="text-sm text-[#555] flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</p>
          ) : (
            <div className="border border-[#E0E0D8] bg-white">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-[#0A0A0A]">
                    <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Name</th>
                    <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Email</th>
                    <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-[#E0E0D8] hover:bg-[#F4F4F0]" data-testid={`user-row-${u.id}`}>
                      <td className="py-3 px-4 text-sm">{u.name}</td>
                      <td className="py-3 px-4 font-mono text-xs">{u.email}</td>
                      <td className="py-3 px-4">
                        <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-[#0A0A0A]/20 bg-[#F4F4F0]">
                          {u.role.replace("_", " ")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
